import Button from "@mui/material/Button";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import React from "react";

// Define allowed sizes and variants
type ButtonSize = "medium" | "large";
type ButtonVariant =
  | "contained"
  | "outlined"
  | "report"
  | "grey"
  | "dark"
  | "disabled";

interface PimoButtonProps {
  size: ButtonSize;
  variant: ButtonVariant;
  text: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const getMuiProps = (variant: ButtonVariant) => {
  switch (variant) {
    case "contained":
      return { variant: "contained" as const, sx: { color: "#fff" } };
    case "outlined":
      return { variant: "outlined" as const, color: "primary" as const };
    case "report":
      return {
        sx: { backgroundColor: "#F86200", color: "#fff" },
      };
    case "grey":
      return { variant: "contained" as const, color: "inherit" as const };
    case "dark":
      return {
        sx: { backgroundColor: "#374151", color: "#fff" },
      };
    case "disabled":
      return {
        variant: "contained" as const,
        color: "primary" as const,
        disabled: true,
      };
    default:
      return { variant: "contained" as const, color: "primary" as const };
  }
};

export const PMButton: PimoReactComponent<PimoButtonProps, "click"> = ({
  size = "medium",
  variant,
  text,
  icon,
  fireEvent,
  onClick,
}) => {
  const muiProps = getMuiProps(variant);
  const isDisabled = variant === "disabled" || muiProps.disabled;

  return (
    <Button
      {...muiProps}
      startIcon={icon}
      disabled={isDisabled}
      onClick={
        isDisabled
          ? undefined
          : () => {
              if (onClick) onClick();
              if (fireEvent) fireEvent("click");
            }
      }
      sx={{
        ...(size === "large"
          ? { padding: "16px 32px" }
          : { padding: "0px 12px" }),

        borderRadius: "4px",
        ...muiProps.sx,
        textTransform: "none",
        boxShadow: "none",
        fontSize: "14px",
        height: size === "large" ? "48px" : "36px",
        transition: "background-color 0.2s, color 0.2s",
        "&:hover": {
          boxShadow: "none",
          backgroundColor:
            variant === "report"
              ? "#ff8c42"
              : variant === "grey"
                ? "#e0e0e0"
                : variant === "dark"
                  ? "#1f2937"
                  : undefined,
        },
      }}
    >
      {text}
    </Button>
  );
};
